import { joinImagesUrl } from '../../../shared/utils/home-blocks.utils';
import type { PublicEvent, StoreEvent } from './events.model';

// Adaptador payload (snake_case) → contrato de la vista (camelCase).
//
// Mismo criterio que `game-section.mappers`: aquí solo se traduce y se
// normaliza lo que el tipo de la vista no admite. Las reglas de negocio
// ("próximo", "del mes", el recorte a 6) las decidió el backend; duplicarlas
// aquí produciría dos implementaciones que acabarían divergiendo.

/** `null`/vacío → `undefined`, que es lo que los campos opcionales de la vista esperan. */
function optionalText(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * El precio llega como entero en colones o `null`. Se blinda contra un `NaN` o
 * un string numérico —`null` es un estado legítimo del contrato ("gratis o sin
 * precio publicado"), pero un valor no numérico solo puede ser una divergencia,
 * y es preferible no pintar precio a pintar `₡NaN`.
 */
function toPrice(value: number | null | undefined): number | null {
  if (value == null) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * El juego solo viaja en `/upcoming`. Se exige `slug` **y** `name`: con uno de
 * los dos vacío no se puede ni pintar el chip ni compararlo contra la landing,
 * y arrastrarlo a medias produciría un chip en blanco. Ausente o incompleto →
 * `undefined`, que la vista trata como evento de tienda.
 */
function toEventGame(
  game: PublicEvent['game'],
): StoreEvent['game'] {
  const slug = optionalText(game?.slug);
  const name = optionalText(game?.name);
  return slug && name ? { slug, name } : undefined;
}

/**
 * La imagen llega relativa (`images/store-events/<file>`) o absoluta `https://…`
 * (D16). Se resuelve con `joinImagesUrl`, la misma regla que usan las imágenes
 * de las secciones de juego: dos copias de la normalización acabarían divergiendo.
 */
function toImageUrl(path: string | null | undefined): string | undefined {
  const trimmed = optionalText(path);
  return trimmed ? joinImagesUrl(trimmed) : undefined;
}

/** Un evento del payload público → evento listo para pintar. */
export function toStoreEvent(event: PublicEvent): StoreEvent {
  const game = toEventGame(event.game);
  // Texto plano (D15): solo se recortan los extremos; los saltos de línea
  // internos se conservan y la vista los respeta con `white-space: pre-line`.
  const description = optionalText(event.description);
  const imageUrl = toImageUrl(event.image_url);
  return {
    id: String(event.id),
    name: event.name,
    startsAt: event.starts_at,
    endsAt: optionalText(event.ends_at),
    type: event.type,
    format: optionalText(event.format),
    price: toPrice(event.price),
    location: event.location,
    url: optionalText(event.url),
    // Claves omitidas, no `undefined`, como `game` (D18): son opcionales en el
    // payload y el objeto de la vista no debe inventarlas.
    ...(description ? { description } : {}),
    ...(imageUrl ? { imageUrl } : {}),
    // Clave omitida, no `game: undefined`: las tres superficies congeladas no
    // lo emiten y el objeto de la vista no debe inventar la clave.
    ...(game ? { game } : {}),
  };
}

/**
 * Lista completa de eventos. Tolera que `data` no sea un array (respuesta
 * truncada, error servido con 200) y descarta las filas sin lo mínimo para
 * pintarse: sin `starts_at` no hay ni fecha ni casilla de calendario donde
 * ponerlas, y arrastrarlas ensuciaría todas las vistas río abajo.
 */
export function toStoreEvents(data: readonly PublicEvent[] | null | undefined): StoreEvent[] {
  if (!Array.isArray(data)) return [];
  return data
    .filter((event): event is PublicEvent => Boolean(event?.starts_at && event?.name))
    .map(toStoreEvent);
}
